/**
 * Sprint V104 (06.10.2026) + Design-Angleichung (Di 06.10.2026, Punkt a) —
 * Pocket-Coach-Einladungs-Seite.
 *
 * Zielgruppe: persoenliche Kontakte von Andreas, die er direkt anschreibt.
 * Der Link kommt aus Mail oder DM, meist auf dem Handy geoeffnet. Deshalb
 * mobile-first, eine Spalte, max-width ~520 px — und ab jetzt im gleichen
 * Design-Vokabular wie die Landing-Page und Challenge-Join: warmes Cream
 * (`#FAF7F2`), Orange-CTA (`var(--fts-accent)`), gelber Akzent-Underline
 * unter dem Keyword, Micro-Tagline-Chip oben, grosse Headline.
 *
 * URL-Form: /einladung?fn=Petra
 *   - `fn` ist optional. Fehlt es, laeuft die Seite ohne Namens-Anrede.
 *   - KEIN Token-Signing. Zugang zum geschenkten Monat laeuft ueber einen
 *     separaten Ablefy-Coupon (nicht in dieser App).
 *
 * Backend-Endpoint: `POST /api/invite/pocket-coach/accept`
 *   -> `activate_free_account(source="pocket_coach_invite")`
 *   -> Session-Cookies -> redirect auf /dashboard
 *
 * Bewusst NICHT in dieser Seite:
 *   kein Preis, kein Checkout, kein Gutschein-Feld, kein Verweis auf
 *   Challenge/Webinar/Workshop, kein Empfehlungs-Block.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

// Marken-Hintergrund — identisch zu HeroHookSection + ChallengeJoin-Variante,
// damit die Seite als Flex4Success wiedererkennbar ist.
const BRAND_CREAM = "#FAF7F2";

export default function EinladungPocketCoach() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // `fn` aus der URL — nur fuer die Begruessung + Formular-Vorbelegung.
  // Sanitize analog zum Backend (`_clean_first_name`): nur Buchstaben,
  // Umlaute, Akzente, Bindestrich, Leerzeichen, Punkt. Max 80 Zeichen.
  const fnFromUrl = useMemo(() => {
    const raw = (searchParams.get("fn") || "").trim();
    const cleaned = raw.replace(/[^\wÀ-ÿäöüÄÖÜß\-\s.]/g, "").slice(0, 80);
    return cleaned || "";
  }, [searchParams]);

  const [firstName, setFirstName] = useState(fnFromUrl);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [checkInbox, setCheckInbox] = useState(false);

  // Browser-Tab-Titel + GA4-Seitenaufruf (einmalig pro Mount). Social-
  // Media-Scraper lesen stattdessen die statischen OG-Tags aus
  // `public/index.html` (ebenfalls auf diese Seite abgestimmt).
  useEffect(() => {
    try {
      document.title = "Dein Access zu Flex4Success";
    } catch (_e) { /* noop */ }
    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", "pocket_coach_invite_view", {
          has_firstname: Boolean(fnFromUrl),
        });
      }
    } catch (_e) { /* analytics-only */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fnFromUrl && !firstName) setFirstName(fnFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fnFromUrl]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      setError("Bitte eine gültige Mail-Adresse eingeben.");
      return;
    }
    if (!consent) {
      setError("Bitte Einverständnis bestätigen.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/invite/pocket-coach/accept", {
        email: trimmedEmail,
        first_name: firstName.trim() || null,
        consent: true,
      });
      try {
        if (typeof window.gtag === "function") {
          window.gtag("event", "pocket_coach_invite_submit", {
            created: Boolean(data?.created),
          });
        }
      } catch (_e) { /* analytics-only */ }
      if (data?.check_inbox) {
        setCheckInbox(true);
        setSubmitting(false);
        return;
      }
      navigate(data?.redirect_to || "/dashboard", { replace: true });
    } catch (_err) {
      setError("Da ist etwas schiefgegangen. Bitte noch einmal versuchen.");
      setSubmitting(false);
    }
  }

  const greetingName = (firstName || fnFromUrl || "").trim();

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: BRAND_CREAM }}
      data-testid="invite-pocket-coach-page"
    >
      {/* Weicher radialer Lichtfleck oben — gleiches Rezept wie HeroHookSection,
          verankert die Seite im Marken-Vokabular. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[520px]"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(243,101,29,0.08) 0%, rgba(243,101,29,0) 55%)",
        }}
      />

      <div className="relative mx-auto w-full max-w-[560px] px-5 py-10 sm:py-14">
        {/* Micro-Tagline-Chip — identisches Rezept zu HeroHookSection:55 */}
        <div className="flex justify-center">
          <span
            className="inline-flex items-center gap-2 rounded-full bg-white border border-amber-300/70 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-amber-700 shadow-sm"
            data-testid="invite-tagline"
          >
            <span
              className="w-1.5 h-1.5 rounded-full bg-amber-500"
              aria-hidden="true"
            />
            Persönliche Einladung · PocketCoach
          </span>
        </div>

        {/* Portrait — groesser als zuvor (176 px), mit weichem Schatten, kein harter Ring. */}
        <div
          className="mx-auto mt-7 h-44 w-44 overflow-hidden rounded-full shadow-2xl ring-4 ring-white"
          data-testid="invite-andreas-photo"
        >
          <img
            src="/images/andreas-portrait.jpg"
            alt="Andreas Jung"
            className="h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              const parent = e.currentTarget.parentElement;
              if (parent && !parent.querySelector("[data-fallback]")) {
                const div = document.createElement("div");
                div.setAttribute("data-fallback", "true");
                div.className =
                  "flex h-full w-full items-center justify-center bg-[var(--fts-primary-deep)] text-4xl font-bold text-white";
                div.textContent = "AJ";
                parent.appendChild(div);
              }
            }}
          />
        </div>

        {/* H1 — gross, Dark Navy, Yellow-Underline unter "Access" (Keyword) */}
        <h1
          className="mt-7 text-center text-4xl sm:text-5xl font-extrabold leading-[1.1] tracking-tight text-[var(--fts-text-primary)]"
          data-testid="invite-headline"
        >
          {greetingName ? (
            <>
              <span>{greetingName},</span>
              <br />
              <span>
                dein{" "}
                <span className="relative inline-block">
                  Access
                  <span
                    className="absolute left-0 right-0 -bottom-0.5 h-[3px] rounded-full bg-amber-400"
                    aria-hidden="true"
                  />
                </span>{" "}
                zu Flex4Success
              </span>
            </>
          ) : (
            <>
              Dein{" "}
              <span className="relative inline-block">
                Access
                <span
                  className="absolute left-0 right-0 -bottom-0.5 h-[3px] rounded-full bg-amber-400"
                  aria-hidden="true"
                />
              </span>{" "}
              zu Flex4Success
            </>
          )}
        </h1>

        {/* Kontext — Du-Ansprache, neutral (allgemein verwendbar).
            Sprint V104 Nachtrag (06.10.2026): die urspruengliche
            Coaching-Fassung („aus unseren Gespraechen") ist durch eine
            neutrale ersetzt. Falls fuer die Coaching-Gruppe eine
            persoenlichere Variante gebraucht wird, laesst sich hier ein
            zweiter Absatz davor stellen — siehe Finish-Note. */}
        <div
          className="mt-6 space-y-4 text-center text-lg leading-relaxed text-slate-700"
          data-testid="invite-context"
        >
          <p>
            Vier Muster. Die meisten Gespräche, die sich festfahren, drehen
            sich um dieselben vier. Der PocketCoach zeigt dir, welches dir
            gerade gegenübersteht — und was du sagst, wenn es darauf ankommt.
          </p>
        </div>

        {/* Pocket-Coach-Block — integriert statt abgesetzte Karte */}
        <div
          className="mt-10 rounded-2xl bg-amber-50/60 border border-amber-100 px-5 py-5"
          data-testid="invite-what-it-is"
        >
          <h2 className="text-base font-bold text-[var(--fts-text-primary)]">
            Dein PocketCoach
          </h2>
          <p className="mt-2 text-base leading-relaxed text-slate-700">
            Im Browser auf Desktop und Handy — überall dieselbe Oberfläche.
            Du öffnest es und bist drin.
          </p>
        </div>

        {/* Outcome-Punkte — Checkmarks in Dark Navy, grosse Schrift */}
        <ul className="mt-7 space-y-4" data-testid="invite-outcomes">
          {[
            "Deine Fokus-Themen sortieren, sobald sie aufkommen — statt sie liegenzulassen.",
            "Für Entscheidungen vor einem Gespräch den passenden Impuls holen.",
            "Nach einem anstrengenden Tag in zwei Minuten runterkommen und neu ausrichten.",
          ].map((text, idx) => (
            <li key={idx} className="flex gap-3">
              <CheckCircle2
                className="mt-0.5 h-6 w-6 flex-shrink-0 text-[var(--fts-primary-deep)]"
                aria-hidden="true"
              />
              <span className="text-base leading-relaxed text-slate-800">
                {text}
              </span>
            </li>
          ))}
        </ul>

        {checkInbox && (
          <div className="mt-10 rounded-2xl bg-white border border-amber-200 px-5 py-6 text-center" data-testid="invite-check-inbox">
            <p className="text-lg font-bold text-[var(--fts-text-primary)]">Schau in dein Postfach.</p>
            <p className="mt-2 text-sm text-slate-600">Wir haben dir einen Login-Link geschickt. Über diesen Link kommst du in deinen PocketCoach.</p>
          </div>
        )}

        {/* Formular */}
        {!checkInbox && <form
          onSubmit={handleSubmit}
          className="mt-10 space-y-4"
          data-testid="invite-form"
        >
          <div>
            <label
              htmlFor="invite-firstname"
              className="block text-sm font-semibold text-[var(--fts-text-primary)]"
            >
              Dein Vorname
            </label>
            <input
              id="invite-firstname"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Vorname"
              maxLength={80}
              autoComplete="given-name"
              className="mt-1 w-full rounded-xl border border-[var(--fts-neutral-200)] bg-white px-4 py-3.5 text-base text-[var(--fts-text-primary)] shadow-sm focus:border-[var(--fts-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--fts-accent)]/20"
              data-testid="invite-firstname-input"
            />
          </div>
          <div>
            <label
              htmlFor="invite-email"
              className="block text-sm font-semibold text-[var(--fts-text-primary)]"
            >
              Deine Mail-Adresse
            </label>
            <input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="du@beispiel.de"
              required
              autoComplete="email"
              inputMode="email"
              className="mt-1 w-full rounded-xl border border-[var(--fts-neutral-200)] bg-white px-4 py-3.5 text-base text-[var(--fts-text-primary)] shadow-sm focus:border-[var(--fts-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--fts-accent)]/20"
              data-testid="invite-email-input"
            />
          </div>

          {/* DSGVO-Consent */}
          <label className="flex items-start gap-3 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-[var(--fts-accent)] focus:ring-[var(--fts-accent)]"
              data-testid="invite-consent-checkbox"
            />
            <span>
              Ich möchte Flex4Success nutzen und bin einverstanden, dass Andreas
              mir dazu gelegentlich schreibt.
            </span>
          </label>

          {error && (
            <div
              className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
              data-testid="invite-error"
            >
              {error}
            </div>
          )}

          {/* CTA — Orange, Pill, Pfeil wandert bei Hover (Marken-Style) */}
          <button
            type="submit"
            disabled={submitting}
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--fts-accent)] px-6 py-4 text-lg font-bold text-white shadow-lg transition-all hover:opacity-95 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="invite-submit-button"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                <span>Einen Moment …</span>
              </>
            ) : (
              <>
                <span>PocketCoach öffnen</span>
                <ArrowRight
                  className="h-5 w-5 transition-transform group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </>
            )}
          </button>

          {/* Fakten-Row unter CTA — Marken-Rezept, allgemein verwendbare
              Formulierung (stimmt mit und ohne Gutschein). */}
          <div
            className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-slate-500"
            data-testid="invite-facts"
          >
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-amber-500" aria-hidden="true" />
              Kostenlos starten
            </span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-amber-500" aria-hidden="true" />
              Du bist sofort drin
            </span>
          </div>
        </form>}

        {/* Hinweis unten — allgemein verwendbar, verweist zurueck auf die
            persoenliche Nachricht. Sprint V104 Nachtrag (06.10.2026):
            urspruenglich stand hier „Deinen Monat auf mich findest du …" —
            das band die Seite an den Gutschein-Fall. Jetzt neutral. */}
        <div
          className="mt-8 rounded-2xl bg-white/70 border border-amber-100 px-5 py-4 text-center text-sm leading-relaxed text-slate-600"
          data-testid="invite-gift-hint"
        >
          Alles Weitere steht in der Nachricht, über die du hierher gekommen bist.
        </div>
      </div>
    </div>
  );
}
